<template>
  <div class="chat-box-wrapper">
    <div class="container">
      <div class="chat-container">
        <div class="chatlist-box" :class="{ view: !isActive }">
          <ChatList
            ref="chatlist"
            :selected-conversation="selectedConversation"
            @conversation-clicked="handleConversationClick"
            @scroll-bottom="handleContactScroll"
            @change-filters="changeContactListFilters"
            @clear-conversation="handleClearConversation"
          />
        </div>
        <div class="chat-box" :class="{ view: isActive }">
          <ChatConvo
            ref="chat"
            :selected-conversation="selectedConversation"
            :typing="typing"
            :scroll="scroll"
            :force-scroll="forceScroll"
            :inactive-users-shown="ignoredOrSavedUsersShown"
            @listview="changeToList"
            @type="handleMessageType"
            @send-message="handleMessageSend"
            @clear-conversation="handleClearConversation"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatList from "./chatlist";
import ChatConvo from "./chatconvo/chatconvo";
import { mapGetters } from "vuex";
import { Chat, Message } from "@/models";

const STORE_KEY = "chatStore";
const COACH_STORE_KEY = "coachStore";
const ATHLETE_STORE_KEY = "athleteStore";

export default {
  name: "chat",
  components: {
    ChatList,
    ChatConvo
  },
  data() {
    return {
      selectedConversation: null,
      typing: false,
      scroll: 0,
      forceScroll: 0,
      receiverId: null,
      receiverType: null,
      isActive: false,
      contactListFilters: {},
      openRouteConversation: false,
      typingInstance: 0,
    };
  },
  computed: {
    ...mapGetters({
      contactList: `${STORE_KEY}/contactList`,
      messages: `${STORE_KEY}/messages`,
      contactListPage: `${STORE_KEY}/contactListPage`,
      currentContacts: `${STORE_KEY}/currentContacts`,
      contacts: `${STORE_KEY}/contacts`,
      singleCoach: `${COACH_STORE_KEY}/singleCoach`,
      athlete: `${ATHLETE_STORE_KEY}/athlete`,
      receiverProfile: `${STORE_KEY}/receiverProfile`,
      currentPage: `${STORE_KEY}/currentPage`
    }),
    inActiveUsersShown() {
      return (
        this.contactListFilters.contactIsBlocked ||
        this.contactListFilters.contactIsIgnored ||
        this.contactListFilters.isContactSaved
      );
    },
    ignoredOrSavedUsersShown() {
      return (
        this.contactListFilters.contactIsIgnored ||
        this.contactListFilters.isContactSaved
      );
    }
  },
  async mounted() {
    await this.$store.dispatch(`${STORE_KEY}/setContactListPage`, 1);
    await this.$store.dispatch(`${STORE_KEY}/clearContactList`);
    this.chatSockets.clearEvents();
    if (this.$route.params.receiverInfo) {
      const receiverInfo = JSON.parse(atob(this.$route.params.receiverInfo));
      this.receiverId = receiverInfo.receiver_id;
      this.receiverType = receiverInfo.receiver_type;
      this.openRouteConversation = true;
    }
    this.chatSockets.addEvents([
      { name: "receiverDetail", handler: this.setReceiverDetail },
      { name: "typingReceive", handler: this.handleServerTyping },
      { name: "messageToClients", handler: this.handleMessageToClients },
      { name: "senderEmit", handler: this.handleContactUpdate },
      { name: "receiverEmit", handler: this.handleReceiverEmit },
      { name: "profileIsPaid", handler: this.getSenderProfile },
      { name: "messageread", handler: this.handleSeenMessage },
      { name: "updatedToPaid", handler: this.handleUserUpdateToPaid },
      { name: "refreshContactList", handler: this.handleUserOfflineOnline },
      { name: "refreshBlockEvent", handler: this.handleRefreshBlock },
      { name: "senderNotificationCount", handler: this.changeNotificationCount },
      {
        name: "refreshDeactivateEvent",
        handler: this.handleUserActivateDeactivate
      },
    ]);
    this.getContactList({});
    this.getSenderProfile();
  },
  beforeDestroy() {
    this.selectedConversation = null;
    this.$store.dispatch(`${STORE_KEY}/deleteReceiverProfile`);
    this.chatSockets.emit("leaveRoom");
    this.$store.dispatch(`${STORE_KEY}/resetStore`);
  },
  methods: {
    changeToList() {
      this.isActive = false;
    },
    setReceiverDetail(data) {
      if (data.receiverHasRead) {
        this.handleMessageRead()
      }
      this.$store.dispatch(`${STORE_KEY}/setReceiverProfile`, {...data.data, lastOnlineDate: (data.senderData && data.senderData.lastOnlineDate ) ? data.senderData.lastOnlineDate : '' });
      if (
        !this.contacts.find(
          list => list.receiverId.Id === this.receiverProfile._id
        )
      ) {
        this.setupNewReceiverAndSelectConversation(data);
      } else {
        this.handleConversationClick(
          this.contacts.find(
            list => list.receiverId.Id === this.receiverProfile._id
          )
        );
      }
    },
    async setupNewReceiverAndSelectConversation(data) {
      let contactIsBlocked = false
      if(data.receiverData && data.receiverData.blockBy.includes(data.data.id)) { 
        contactIsBlocked = true
      }
      await this.$store.dispatch(`${STORE_KEY}/unshiftContactList`, { contactIsBlocked: contactIsBlocked, blockBy: data.receiverData && data.receiverData.blockBy || [] });
      if (!this.openRouteConversation) return;

      this.handleConversationClick(this.contacts[0]);
      this.openRouteConversation = false;
    },
    async handleConversationClick(conversation) {
      if (
        this.selectedConversation &&
        this.selectedConversation.id === conversation.id
      ) {
        this.isActive = true;
        return;
      }
      this.isActive && await this.chatSockets.emit("leaveRoom");
      this.selectedConversation = conversation;
      if (
        this.receiverProfile._id !== this.selectedConversation.receiverId.Id
      ) {
        await this.chatSockets.emit("joinRoom", {
          senderId: this.selectedConversation.senderId,
          receiverId: this.selectedConversation.receiverId.Id,
          senderType: Chat.senderType(),
          receiverType: this.selectedConversation.onModel,
          lastMessageId: this.selectedConversation.lastMessageId.Id,
          lastMessageSenderId: this.selectedConversation.lastMessageId.msgFrom
        });
      }
      await this.$store.dispatch(`${STORE_KEY}/emptyMessages`);
      const room = [
        this.selectedConversation.senderId,
        this.selectedConversation.receiverId.Id
      ]
        .sort()
        .join("");
      if (this.selectedConversation.id)
        await this.$store.dispatch(`${STORE_KEY}/getMessages`, {
          room,
          sort: "-createdAt",
          userId: this.selectedConversation.senderId,
          limit: Message.LIMIT,
          page: this.currentPage
        });
      this.handleScrollIndexIncrease();
      this.isActive = true;
    },
    handleMessageType() {
      this.chatSockets.emit("typing", {
        senderId: this.selectedConversation.senderId,
        receiverId: this.selectedConversation.receiverId.Id
      });
    },
    handleMessageSend(message) {
      this.selectedConversation = new Chat({
        ...this.selectedConversation,
        isRead: false,
        allMessegeRead: false
      });
      this.chatSockets.emit("newMessageToServer", {
        senderId: this.$route.params.profileId,
        receiverId: this.selectedConversation.receiverId.Id,
        text: message,
        room: [
          this.$route.params.profileId,
          this.selectedConversation.receiverId.Id
        ]
          .sort()
          .join(""),
        senderType: Chat.senderType(),
        receiverType: this.selectedConversation.onModel,
        receiverName:
          this.receiverProfile.coachname || this.receiverProfile.athletename,
        senderName: this.onlyPlayer()
          ? this.athlete.athletename
          : this.singleCoach.coachname
      });
    },
    async handleServerTyping() {
      if (this.ignoredOrSavedUsersShown)
        return

      this.typingInstance += 1
      this.typing = true

      if (this.typingInstance === 1) {
         setTimeout(() => { 
          this.typing = false
          this.typingInstance = 0
        }, 2500);
      }
     
    },
    async handleMessageToClients(msg) {
      if (
        msg.msgFrom !== this.selectedConversation.receiverId.Id &&
        msg.msgTo !== this.selectedConversation.receiverId.Id
      )
        return;
      await this.$store.dispatch(`${STORE_KEY}/pushMessage`, msg);
      if (this.inActiveUsersShown && msg.msgFrom === this.$route.params.profileId) {
        this.contactListFilters = {};
        this.$refs.chatlist.clearLabels();
        // this.$refs.chatlist.hideContactSearch();
      }
      this.$nextTick(() => {
        this.scroll++;
      });
    },
    handleScrollIndexIncrease() {
      this.forceScroll++;
    },
    async handleContactUpdate(userContact, notificationCount, handleMessageRead) {
      await this.$store.dispatch(
        `${STORE_KEY}/setNotificationCount`,
        notificationCount
      );
      if (this.inActiveUsersShown) return;
      await this.$store.dispatch(`${STORE_KEY}/updateContact`, userContact);
      if (
        this.selectedConversation &&
        this.selectedConversation.id === userContact._id
      )
        this.selectedConversation = new Chat({ ...userContact, isRead: false, allMessegeRead: false });
      if (handleMessageRead) {
        this.handleMessageRead()
      }
    },
    async handleReceiverEmit(userContact, notificationCount) {
      await this.$store.dispatch(
        `${STORE_KEY}/setNotificationCount`,
        notificationCount
      );
      if (this.inActiveUsersShown) return;
      await this.$store.dispatch(`${STORE_KEY}/updateContact`, userContact);
      if (
        this.selectedConversation &&
        this.selectedConversation.id === userContact._id
      )
        this.selectedConversation = new Chat({ ...userContact });
    },
    startConversationWithReceiver() {
      this.chatSockets.emit("joinRoom", {
        senderId: Chat.profileId(),
        receiverId: this.receiverId,
        senderType: Chat.senderType(),
        receiverType: this.receiverType,
        lastMessageId: null,
        lastMessageSenderId: null
      });
      this.$store.dispatch(`${STORE_KEY}/emptyMessages`);
    },
    async getContactList(filters = {}) {
      if (this.currentContacts.length === 0 && this.contactListPage !== 1)
        return;

      await this.$store.dispatch(`${STORE_KEY}/getContactList`, {
        page: this.contactListPage,
        contactIsBlocked: false,
        contactIsIgnored: false,
        isContactSaved: false,
        isConversationCleared: filters.q ? null : false,
        ...filters
      });
      if (
        this.$route.params.receiverInfo &&
        !this.selectedConversation &&
        !this.inActiveUsersShown
      ) {
        this.startConversationWithReceiver();
        return;
      }
    },
    handleClearConversation() {
      this.selectedConversation = null;
      this.$store.dispatch(`${STORE_KEY}/deleteReceiverProfile`);
      this.chatSockets.emit("leaveRoom");
    },
    async handleSeenMessage(notificationCount) {
      if (!this.selectedConversation.id) {
        this.$store.dispatch(`${STORE_KEY}/emptyMessages`);
        return;
      }
      this.selectedConversation = new Chat({
        ...this.selectedConversation,
        isRead: true,
        allMessegeRead: true
      });
      this.$store.dispatch(
        `${STORE_KEY}/setReadToTrue`,
        this.selectedConversation
      );
      this.$store.dispatch(
        `${STORE_KEY}/setNotificationCount`,
        notificationCount
      );
    },
    async changeNotificationCount(notificationCount) {
      await this.$store.dispatch(`${STORE_KEY}/setNotificationCount`, notificationCount)
    },
    getSenderProfile() {
      this.$store.dispatch(`${STORE_KEY}/getSenderProfile`);
    },
    handleUserUpdateToPaid(userContact) {
      this.$store.dispatch(
        `${STORE_KEY}/conditionallySpliceContact`,
        userContact
      );
      if (this.selectedConversation.id === userContact._id)
        this.selectedConversation = new Chat({ ...userContact });
    },
    handleRefreshBlock(contact) {
      this.$store.dispatch(`${STORE_KEY}/setBlockedToTrue`, contact);
    },
    handleContactScroll() {
      if (this.currentContacts.length === 0) return;

      this.$store.dispatch(`${STORE_KEY}/increaseContactListPage`);
      this.$store.dispatch(`${STORE_KEY}/getContactList`, {
        page: this.contactListPage
      });
    },
    changeContactListFilters(filters) {
      this.contactListFilters = { ...this.contactListFilters, ...filters };
    },
    async handleUserActivateDeactivate(userContact) {
      await this.$store.dispatch(
        `${STORE_KEY}/conditionallySpliceContact`,
        userContact
      );
      if (this.selectedConversation.id === userContact._id)
        this.selectedConversation = new Chat({ ...userContact });
    },
    handleUserOfflineOnline(receiverId, type) {
      this.$store.dispatch(`${STORE_KEY}/toggleUserOnlineOffline`, {
        receiverId: receiverId,
        status: type
      });
      this.updateSelectedConversation();
    },
    updateSelectedConversation() {
      if (this.selectedConversation)
        this.selectedConversation = this.contacts.find(
          cont => cont.Id === this.selectedConversation.Id
        );
    },
    handleMessageRead() {
      if (this.selectedConversation) {
        this.selectedConversation = new Chat({
          ...this.selectedConversation,
          isRead: true,
          allMessegeRead: true
        });
        this.$store.dispatch(
          `${STORE_KEY}/setReadToTrue`,
          this.selectedConversation
        );
      }
    }
  },
  watch: {
    contactList(newVal) {
      const conversation = newVal.find(
        conv =>
          conv.id ===
          (this.selectedConversation && this.selectedConversation.id)
      );
      this.selectedConversation = conversation
        ? new Chat({ ...conversation })
        : this.selectedConversation;
    },
    contactListFilters: {
      deep: true,
      handler: function(newVal) {
        this.$store.dispatch(`${STORE_KEY}/setContactListPage`, 1);
        this.$store.dispatch(`${STORE_KEY}/clearContactList`);
        this.getContactList(newVal);
      }
    }
  }
};
</script>

<style lang="scss">
.chat-box-wrapper {
  padding: 15px 0;
}
.chat-container {
  display: flex;
  flex: 1 1 auto;
  height: 100%;
  box-shadow: 0 7px 20px 2px rgba(0, 0, 0, 0.3);
  .chatlist-box {
    flex: 0 0 22%;
  }
  .chat-box {
    flex: 1;
  }
  .user-avatar {
    position: relative;
    display: inline-block;
    border-radius: 50%;
    overflow: hidden;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  input {
    background-color: #ffffff;
    border: 1px solid #ffffff !important;
    border-radius: 21px;
  }
}
@media only screen and (max-width: 1600px) {
  .chat-container {
    .chatlist-box {
      flex: 0 0 30%;
    }
  }
}
@media only screen and (max-width: 575px) {
  .chat-container {
    flex-direction: column;
    height: calc(100vh - 94px);
    min-height: 548px;
    position: relative;
    .chatlist-box {
      flex: 0 0 100%;
      width: 100%;
      position: absolute;
      top: 0;
      left: 0;
      opacity: 0;
      z-index: 0;
      transition: all 0.2s ease-in-out;
      &.view {
        z-index: 1;
        opacity: 1;
      }
    }
    .chat-box {
      position: absolute;
      width: 100%;
      position: absolute;
      top: 0;
      left: 0;
      opacity: 0;
      z-index: 0;
      &.view {
        z-index: 1;
        opacity: 1;
      }
    }
  }
}

@media only screen and (min-width: 1600px) {
  .chat-container {
    .chatlist-box {
      flex: 0 0 28%;
    }
  }
}
</style>
